import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Consumptions, RLMConsumptions, SLPConsumptions } from '@app/shared/models/meter-data/meter-consumption';
import { MeterConsumption } from '@app/shared/constants';
import { MeterType } from '@app/shared/enums';
import { MeterConsumptionService } from '@app/shared/services/api/meter-consumption.service';
import { StringUtility } from '@app/shared/string-utility';
import { isNil } from 'lodash';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeAll, mergeMap, reduce } from 'rxjs/operators';
import { ApiService } from './api.service';
import { ComparisonValues } from '@app/efficiency-analysis/models/comparison-values';
import { NewComparisonValues } from '@app/efficiency-analysis/models/new-comparison-values';
import { Buildings } from '@app/energy-guard/models/buildings';
import { EfficiencyAnalysisDto } from '@app/shared/models/efficiency-analysis/efficiency-analysis-dto';
import { EfficiencyAnalysisMeters } from '@app/shared/models/efficiency-analysis/efficiency-analysis-meters';
import { ClimateFactorsDto } from '@app/shared/models/efficiency-analysis/climate-factors';
import { IBuildingEfficiencyData } from '@app/efficiency-analysis/models/buildings/building-efficiency-data';
import { MeterTypeUtility } from '@app/shared/meter-type-utility';

@Injectable({
  providedIn: 'root',
})
export class EfficiencyAnalysisApiService extends ApiService {
  constructor(public http: HttpClient, private meterConsumptionService: MeterConsumptionService) {
    super(http);
  }

  dbColumnNames = [
    'Guid',
    'BuildingTypeId',
    'ReferenceValueHeat',
    'CharacteristicValueHeat',
    'ReferenceValueElectricity',
    'CharacteristicValueElectricity',
    'Number',
    'Unit',
    'ColorHeatMin',
    'ColorHeatMax',
    'ColorElectricityMin',
    'ColorElectricityMax',
    'CreationDate',
    'ModifyDate',
    'ModifyUser',
    'BuildingId',
    'AirconditionedFloorArea',
    'AnnualConsumptionOil',
    'AnnualConsumptionPV',
    'AverageElectricityConsumption',
    'AverageHeatConsumption',
    'ClimateControlledFloorArea',
    'DistrictHeatingConsumption',
    'DistrictHeatingFromDate',
    'DistrictHeatingToDate',
    'EfficencyRAG',
    'EfficiencyAnalysisDate',
    'EfficiencyAnalysisEndDate',
    'EfficiencyAnalysisStartDate',
    'EfficiencyPercentageElectricity',
    'EfficiencyPercentageHeat',
    'EfficiencyRating',
    'ElectricityColorStatus',
    'ElectricityComputedValue',
    'ElectricityConsumption',
    'ElectricityFromDate',
    'ElectricityGuideValue',
    'ElectricityHeatConsumption',
    'ElectricityHeatFromDate',
    'ElectricityHeatToDate',
    'ElectricityMaxDataRange',
    'ElectricityMinDataRange',
    'ElectricityToDate',
    'EnergyCategory',
    'FloorArea',
    'HeatColorStatus',
    'HeatComputedValue',
    'HeatedFloorArea',
    'HeatGuideValue',
    'HeatingOilConsumption',
    'HeatingOilFromDate',
    'HeatingOilToDate',
    'HeatingTypeId',
    'HeatMaxDataRange',
    'HeatMinDataRange',
    'IsAirConditioned',
    'IsNaturelyVented',
    'IsPhotoVoltaicInstalled',
    'LpgConsumption',
    'LpgFromDate',
    'LpgToDate',
    'NaturalGasConsumption',
    'NaturalGasFromDate',
    'NaturalGasToDate',
    'PelletWoodConsumption',
    'PelletWoodFromDate',
    'PelletWoodToDate',
    'VentilatedFloorArea',
    'StartDate',
    'EndDate',
    'IsElectricityValueSuggested',
    'IsHeatValueSuggested',
    'Notes',
  ];

  meterEfficiencyDbColumnNames = ['EfficiencyAnalysisId', 'EnergyTypeName', 'Guid', 'IsSelected', 'MeterNumber', 'Value'];

  public getComparisonValues(): Observable<ComparisonValues[]> {
    return this.http.get<ComparisonValues[]>(this.endpoint(`socrates/meters/api/references/ComparisonValues`));
  }

  public getClimateFactor(postalCode: string): Observable<ClimateFactorsDto[]> {
    return this.http.get<ClimateFactorsDto[]>(this.endpoint(`socrates/meters/api/references`, `ClimateFactors?$filter=postalCode eq ${postalCode}`));
  }

  public updateComparisonValues(data: NewComparisonValues): Observable<ComparisonValues> {
    const payload = this.patchMapColumnNames(data, this.dbColumnNames);
    return this.http.patch<ComparisonValues>(this.endpoint(`socrates/meters/api/references/ComparisonValues/${data.guid}`), payload);
  }

  public getBuildingsByBuildingTypeId(municipalityKey: string, buildingTypeId: string): Observable<Buildings[]> {
    return this.http.get<Buildings[]>(
      this.endpoint(`socrates/meters/api/municipalities`, `${municipalityKey}/buildings?$filter=buildingTypeId eq ${buildingTypeId}`)
    );
  }

  public getConsumption(
    meterId: string,
    meterTypeCode: string,
    deviceEUI: string,
    isStrom: boolean,
    isConsumer: boolean,
    startDate: Date,
    endDate: Date
  ): Observable<number> {
    if (StringUtility.areStringsIdentical(meterTypeCode, MeterType.slpZahler)) {
      if (deviceEUI) {
        // graphql
        return of(0);
      } else {
        return this.calculateSlpConsumptionByYear(meterId, startDate.getFullYear(), endDate.getFullYear());
      }
    } else {
      return this.calculateRlmConsumption(meterTypeCode, isStrom, isConsumer, startDate, endDate, meterId);
    }
  }

  public getByBuildingId(buildingId: string, municipalityKey: string): Observable<EfficiencyAnalysisDto[]> {
    return this.http.get<EfficiencyAnalysisDto[]>(
      this.endpoint(`socrates/meters/api/municipalities`, `${municipalityKey}/EfficiencyAnalysis?$filter=buildingId eq ${buildingId}`)
    );
  }

  public getBuildingEffieciencyByBuildingId(buildingId: string): Observable<IBuildingEfficiencyData[]> {
    return this.http.get<IBuildingEfficiencyData[]>(
      this.endpoint(`socrates/meters/api/views/ViewBuildingMetersExtendedInfo1?$filter=guid eq ${buildingId}&$top=1`)
    );
  }

  public saveEfficiencyAnalysis(analysis: Partial<EfficiencyAnalysisDto>, municipalityKey: string): Observable<EfficiencyAnalysisDto> {
    return !isNil(analysis.guid)
      ? this.http.patch<EfficiencyAnalysisDto>(
          this.endpoint(`socrates/meters/api/municipalities/${municipalityKey}/EfficiencyAnalysis/${analysis.guid}`),
          this.patchMapColumnNames(analysis, this.dbColumnNames)
        )
      : this.http.post<EfficiencyAnalysisDto>(this.endpoint(`socrates/meters/api/municipalities/${municipalityKey}/EfficiencyAnalysis`), analysis);
  }

  public getEfficiencyAnalysisMetersByEfficiencyAnalysisId(efficiencyAnalysisId: string, municipalityKey: string): Observable<EfficiencyAnalysisMeters[]> {
    return this.http.get<EfficiencyAnalysisMeters[]>(
      this.endpoint(encodeURI(`socrates/meters/api/municipalities/${municipalityKey}/EfficiencyAnalysis/${efficiencyAnalysisId}/meters`))
    );
  }

  public deleteEfficiencyAnalysisByBuildngId(buildingId: string, municipalityKey: string): Observable<null> {
    return this.getByBuildingId(buildingId, municipalityKey).pipe(
      mergeAll(),
      mergeMap((analysis: EfficiencyAnalysisDto) =>
        this.getEfficiencyAnalysisMetersByEfficiencyAnalysisId(analysis.guid, municipalityKey).pipe(
          catchError(() => of([] as EfficiencyAnalysisMeters[])),
          mergeMap((meters: EfficiencyAnalysisMeters[]) => (meters.length > 0 ? this.deleteEfficiencyAnalysisMeters(meters, municipalityKey) : of([]))),
          mergeMap(() => this.deleteEfficiencyAnalysis(analysis.guid, municipalityKey))
        )
      )
    );
  }

  public saveEfficiencyAnalysisMeter(meter: Partial<EfficiencyAnalysisMeters>, municipalityKey: string): Observable<EfficiencyAnalysisMeters> {
    return !isNil(meter.guid)
      ? this.http.patch<EfficiencyAnalysisMeters>(
          this.endpoint(`socrates/meters/api/municipalities/${municipalityKey}/EfficiencyAnalysis/${meter.efficiencyAnalysisId}/meters/${meter.guid}`),
          this.patchMapColumnNames(meter, this.meterEfficiencyDbColumnNames)
        )
      : this.http.post<EfficiencyAnalysisMeters>(
          this.endpoint(`socrates/meters/api/municipalities/${municipalityKey}/EfficiencyAnalysis/${meter.efficiencyAnalysisId}/meters`),
          meter
        );
  }

  private deleteEfficiencyAnalysisMeters(meters: EfficiencyAnalysisMeters[], municipalityKey: string): Observable<null[]> {
    return forkJoin(meters.map((meter: EfficiencyAnalysisMeters) => this.deleteEfficiencyAnalysisMeter(meter, municipalityKey))).pipe(
      catchError(() => of([null]))
    );
  }

  private deleteEfficiencyAnalysis(guid: string, municipalityKey: string): Observable<null> {
    return this.http.delete<null>(this.endpoint(`socrates/meters/api/municipalities/${municipalityKey}/EfficiencyAnalysis/${guid}`));
  }

  private deleteEfficiencyAnalysisMeter(meter: EfficiencyAnalysisMeters, municipalityKey: string): Observable<null> {
    return this.http.delete<null>(
      encodeURI(this.endpoint(`socrates/meters/api/municipalities/${municipalityKey}/EfficiencyAnalysis/${meter.efficiencyAnalysisId}/meters/${meter.guid}`))
    );
  }

  private calculateRlmConsumption(
    meterTypeCode: string,
    isStrom: boolean,
    isConsumer: boolean,
    startDate: Date,
    endDate: Date,
    meterId: string
  ): Observable<number> {
    const isSmd = MeterTypeUtility.isSmartMeter(meterTypeCode);
    const obisId = this.determineObisId(isStrom, isSmd, isConsumer);
    const $startConsumptionData: Observable<RLMConsumptions> = this.meterConsumptionService.getRLMMonthlyConsumption(
      meterId,
      startDate.getFullYear(),
      obisId,
      isSmd,
      isStrom
    );
    const $endConsumptionData: Observable<RLMConsumptions | null> =
      startDate.getFullYear() !== endDate.getFullYear()
        ? this.meterConsumptionService.getRLMMonthlyConsumption(meterId, endDate.getFullYear(), obisId, isSmd, isStrom)
        : of(null);

    return forkJoin([$startConsumptionData, $endConsumptionData]).pipe(
      map((resp: [RLMConsumptions, RLMConsumptions | null]): number => {
        let consumption = 0;
        if (!isNil(resp[0])) {
          const rlmConsumptionData = resp[0];
          for (let index = startDate.getMonth(); index < 12; index++) {
            consumption += rlmConsumptionData.consumptions[index]?.value ?? 0;
          }
        }
        if (startDate.getFullYear() !== endDate.getFullYear() && !isNil(resp[1])) {
          const rlmHistoricConsumptionData = resp[1];
          for (let index = 0; index <= endDate.getMonth(); index++) {
            consumption += rlmHistoricConsumptionData.consumptions[index]?.value ?? 0;
          }
        }

        return consumption;
      })
    );
  }

  private determineObisId(isStrom: boolean, isSmd: boolean, isConsumer: boolean): string {
    let obisId: string;
    if (!isStrom) {
      obisId = isSmd ? MeterConsumption.Obis.GasSmd : MeterConsumption.Obis.GasPreliminary;
    } else {
      obisId = isConsumer ? MeterConsumption.NonBillingRelevantObis.consumer : MeterConsumption.NonBillingRelevantObis.producer;
    }
    return obisId;
  }

  private calculateSlpConsumptionByYear(meterId: string, startDateYear: number, endDateYear: number): Observable<number> {
    return this.meterConsumptionService.getSLPYearlyConsumption(meterId, startDateYear, endDateYear).pipe(
      mergeAll(),
      map((data: SLPConsumptions) => data.consumptions.map((consumption: Consumptions) => consumption.value)),
      mergeAll(),
      reduce((total: number, current: number) => total + current, 0)
    );
  }
}
